import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ProfileService } from 'app/configuration/profile/profile.service';
import { NavbarService } from 'app/_services/navbar.service';
import { LOGOUT_REASON_STATE_KEY, logoutReasonMessage } from 'app/_services/session/logout-reason';
import { ErrorCodes } from 'app/shared/errors.enums';
import { SharedModule } from 'app/shared/shared.module';
import { UtilService } from 'app/shared/util.service';
import { environment } from '../../../environments/environment';
import { AuthenticationService, SessionService } from '../../_services/index';
import {
  DEFAULT_EXPIRES_IN_SECONDS,
  DEFAULT_RESEND_AVAILABLE_IN_SECONDS,
  LoginFlowResponse,
  MfaChallenge,
  SessionTokens,
} from './auth-flow.model';
import { isPasswordExpired, toMfaChallenge, toSessionTokens } from './auth-flow.mapper';
import { AuthFlowService } from './auth-flow.service';
import { CredentialsStepComponent } from './credentials-step/credentials-step.component';
import { MfaStepComponent } from './mfa-step/mfa-step.component';
import { PostLoginService } from './post-login.service';
import { SsoContextService } from './sso-context.service';
import { SsoStepComponent } from './sso-step/sso-step.component';
import { TrustedDeviceService } from './trusted-device.service';

type Step = 'email' | 'password' | 'sso' | 'sso-no-account' | 'mfa';

const GENERIC_ERROR = 'errors.login.something_went_wrong';

const LOADER_MS = 1800;

const AUTH_PARAMS = [
  'returnUrl',
  'code',
  'state',
  'iss',
  'scope',
  'authuser',
  'hd',
  'prompt',
  'session_state',
  'error',
  'error_description',
  'token_id',
  'user_id',
  'mcp_transaction'
];

@Component({
  selector: 'mh-auth-login',
  standalone: true,
  imports: [SharedModule, RouterModule, CredentialsStepComponent, SsoStepComponent, MfaStepComponent],
  templateUrl: './auth-login.component.html',
  providers: [ProfileService, PostLoginService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuthLoginComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authFlow = inject(AuthFlowService);
  private readonly postLogin = inject(PostLoginService);
  private readonly ssoContext = inject(SsoContextService);
  private readonly trustedDevice = inject(TrustedDeviceService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly sessionService = inject(SessionService);
  private readonly navbarService = inject(NavbarService);
  private readonly utilService = inject(UtilService);
  private readonly location = inject(Location);
  private readonly destroyRef = inject(DestroyRef);

  readonly loaderVisible = signal(true);
  readonly booting = signal(false);
  readonly step = signal<Step>('email');
  readonly loading = signal(false);
  readonly errorKey = signal<string | null>(null);
  readonly errorParams = signal<Record<string, string | number>>({});
  readonly logoutMessage = signal<string | null>(null);

  readonly email = signal('');
  readonly ssoProvider = signal<string | null>(null);
  readonly challenge = signal<MfaChallenge | null>(null);
  readonly rememberDeviceAllowed = signal(false);

  readonly availableLanguages: string[] = this.utilService.translateLanguages;
  readonly currentLanguage = signal('es');
  readonly demoUrl = signal('');
  readonly needHelpUrl = signal('');

  private returnUrl = '/';
  private queryParams: Record<string, any> = {};
  private customerParam: string;
  private authorizationUrl: string | null = null;
  private ssoState: string | null = null;
  private remainingAttempts: number | null = null;

  private mcpTransaction: string | null = null;

  ngOnInit(): void {
    const params = this.route.snapshot.queryParams;

    // El login para un cliente MCP no toca la sesión web: el localStorage lo comparten todas las pestañas de Fidelity.
    if (!this.isMcpLogin(params)) {
      this.authenticationService.logout();
      this.sessionService.removeCurrentHotelPSConfig();
    }
    this.utilService.forceResize();
    this.navbarService.unsubscribeNavigationEvent();

    this.consumeLogoutReason();

    this.queryParams = Object.keys(params)
      .filter((key) => !AUTH_PARAMS.includes(key))
      .reduce((acc, key) => ({ ...acc, [key]: params[key] }), {});
    this.customerParam = params['customer'];

    this.mcpTransaction = this.validMcpTransaction(params['mcp_transaction']);
    this.returnUrl = this.sanitizeReturnUrl(params['returnUrl'] || '/');

    this.setLanguage(params['language'] || this.utilService.getCurrentLanguage());

    if (params['error']) {
      this.ssoContext.clear();
      this.errorKey.set('auth_login.sso.failed');
      return this.dismissLoader();
    }
    if (params['code'] && params['state']) {
      this.restoreSsoContext(params['state']);
      this.startBooting();
      return this.exchangeSso(params['code'], params['state']);
    }
    if (params['token_id'] && params['user_id']) {
      this.startBooting();
      return this.autoLogin(params['token_id'], params['user_id']);
    }

    this.ssoContext.clear();
    this.dismissLoader();
  }

  handleCredentials({ email, password }: { email: string; password: string }): void {
    this.email.set(email);
    this.clearError();
    this.logoutMessage.set(null);

    if (this.step() === 'email') return this.resolveFlow(email);

    this.loading.set(true);
    this.authFlow
      .loginWithPassword(email, password)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.loading.set(false);

          if (result.status === 'RECOVERY_REQUIRED') return this.goToRecovery(result.recovery, result);
          if (result.status === 'MFA_VERIFICATION_REQUIRED' && result.challenge) {
            this.challenge.set(result.challenge);
            this.step.set('mfa');
            return;
          }
          this.completeLogin(result.tokens);
        },
        error: (error) => this.handleCredentialsError(error),
      });
  }

  handleMfaVerify({ code, rememberDevice }: { code: string; rememberDevice: boolean }): void {
    this.clearError();
    this.loading.set(true);

    this.authFlow
      .verifyMfa(this.challenge().challengeId, code, rememberDevice)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.loading.set(false);
          if (rememberDevice) {
            this.trustedDevice.store(this.email(), result.trustedDeviceToken, result.trustedDeviceExpiresAt);
          }
          this.completeLogin(result.tokens);
        },
        error: (error) => {
          this.loading.set(false);
          this.applyMfaError(error);
        },
      });
  }

  handleMfaResend(): void {
    this.clearError();
    this.loading.set(true);

    this.authFlow
      .resendMfa(this.challenge().challengeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (challenge) => {
          this.loading.set(false);
          this.challenge.set(challenge ?? this.resentChallenge());
        },
        error: () => {
          this.loading.set(false);
          this.errorKey.set(GENERIC_ERROR);
        },
      });
  }

  continueWithSso(): void {
    if (!this.authorizationUrl) return this.errorKey.set('auth_login.sso.failed');

    this.ssoContext.save(this.ssoState, this.email(), {
      returnUrl: this.returnUrl,
      queryParams: this.queryParams,
      customerParam: this.customerParam,
    }, this.mcpTransaction);
    this.loading.set(true);
    window.location.href = this.authorizationUrl;
  }

  goBack(): void {
    this.step.set('email');
    this.ssoProvider.set(null);
    this.authorizationUrl = null;
    this.ssoState = null;
    this.challenge.set(null);
    this.clearError();

    const params = this.route.snapshot.queryParams;
    if (params['code'] || params['state'] || params['error']) {
      void this.router.navigate(['/login'], { queryParams: this.deepLinkParams() });
    }
  }

  clearError(): void {
    this.errorKey.set(null);
    this.errorParams.set({});
  }

  setLanguage(iso: string): void {
    this.currentLanguage.set(iso);
    this.demoUrl.set(this.localizedWidget(iso, 'solicita-tu-demo/', 'en/request-a-demo/', 'pt/solicite-um-contato/'));
    this.needHelpUrl.set(this.localizedWidget(iso, 'necesitas-ayuda/', 'en/need-help/', 'pt/precisa-de-ajuda/'));
    this.utilService.ga('language', 'click', iso);
    this.utilService.setCurrrentLanguage(iso);
  }

  handleLanguageChange(event: Event): void {
    this.setLanguage((event.target as HTMLSelectElement).value);
  }

  trackForgot(): void {
    this.utilService.ga('forgot-password', 'click', this.currentLanguage());
  }

  private localizedWidget(iso: string, es: string, en: string, pt: string): string {
    const path = iso === 'en' ? en : iso === 'pt' ? pt : es;
    return `${environment.widgets}${path}`;
  }

  private sanitizeReturnUrl(raw: string): string {
    const value = raw.startsWith('/') ? raw : `/${raw}`;
    return /^\/[/\\]/.test(value) ? '/' : value;
  }

  private resolveFlow(email: string): void {
    this.loading.set(true);
    this.remainingAttempts = null;

    this.authFlow
      .resolveFlow(email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.loading.set(false);
          this.applyFlow(response);
        },
        error: (error) => {
          this.loading.set(false);
          // no revelamos si la cuenta existe: seguimos al paso de password, que falla igual para un email inexistente
          if (this.isMissingAccount(error)) return this.applyFlow(null);
          this.errorKey.set(GENERIC_ERROR);
        },
      });
  }

  private applyFlow(response: LoginFlowResponse): void {
    this.rememberDeviceAllowed.set(!!response?.mfa?.remember_device_allowed);

    if (response?.flow === 'SSO' && response?.sso?.authorization_url) {
      this.authorizationUrl = response.sso.authorization_url;
      this.ssoState = response.sso.state;
      this.ssoProvider.set(response.sso.provider);
      this.step.set('sso');
      return;
    }
    this.step.set('password');
  }

  private restoreSsoContext(state: string): void {
    const pending = this.ssoContext.take(state);
    if (!pending) return;

    this.email.set(pending.email);
    this.queryParams = pending.queryParams;
    this.customerParam = pending.customerParam;
    this.returnUrl = this.sanitizeReturnUrl(pending.returnUrl);
    this.mcpTransaction = this.validMcpTransaction(pending.mcpTransaction);
  }

  private deepLinkParams(): Record<string, string> {
    const params: Record<string, string> =
    this.returnUrl === '/' ? {} : { ...this.queryParams, returnUrl: this.returnUrl };
    return this.mcpTransaction ? { ...params, mcp_transaction: this.mcpTransaction } : params;
  }

  private exchangeSso(code: string, state: string): void {
    this.step.set('sso');
    this.loading.set(true);

    this.authFlow
      .exchangeSso(code, state)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tokens) => {
          if (!tokens) {
            this.loading.set(false);
            this.booting.set(false);
            return this.step.set('sso-no-account');
          }

          this.ssoProvider.set(tokens.sso_provider);
          this.completeLogin(tokens);
        },
        error: (error) => {
          this.loading.set(false);
          this.booting.set(false);
          if (this.isMissingAccount(error)) return this.step.set('sso-no-account');

          this.step.set('email');
          this.errorKey.set('auth_login.sso.failed');
        },
      });
  }

  private consumeLogoutReason(): void {
    const state = (this.location.getState() ?? {}) as Record<string, unknown>;
    const message = logoutReasonMessage(state[LOGOUT_REASON_STATE_KEY]);
    if (!message) return;

    this.logoutMessage.set(message);

    const { [LOGOUT_REASON_STATE_KEY]: consumed, ...rest } = state;
    this.location.replaceState(this.location.path(true), '', rest);
  }

  private startBooting(): void {
    this.booting.set(true);
    this.loaderVisible.set(false);
  }

  private dismissLoader(): void {
    setTimeout(() => this.loaderVisible.set(false), LOADER_MS);
  }

  private applyMfaError(error: any): void {
    const refreshed = toMfaChallenge(error);
    if (refreshed) this.challenge.set(refreshed);

    if ((refreshed ?? this.challenge())?.remainingAttempts <= 0) {
      this.challenge.set(null);
      this.step.set('email');
      this.errorKey.set('auth_login.mfa.attempts_exhausted');
      return;
    }
    this.errorKey.set('auth_login.mfa.code_wrong');
  }

  private resentChallenge(): MfaChallenge {
    const current = this.challenge();
    return {
      ...current,
      expiresInSeconds: DEFAULT_EXPIRES_IN_SECONDS,
      resendAvailableInSeconds: DEFAULT_RESEND_AVAILABLE_IN_SECONDS,
      remainingResends: isFinite(current.remainingResends) ? current.remainingResends - 1 : current.remainingResends,
    };
  }

  private isMissingAccount(error: any): boolean {
    const message = `${error?.message ?? ''} ${error?.error_message ?? ''}`.toLowerCase();
    return error?.status === 404 || message.includes('user not found') || message.includes('not exist');
  }

  private autoLogin(tokenId: string, userId: string): void {
    this.authenticationService
      .loginFromTokenId(userId, tokenId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response?.code !== 200) {
            this.booting.set(false);
            this.errorKey.set(GENERIC_ERROR);
            return;
          }
          this.completeLogin(toSessionTokens(response));
        },
        error: (error) => {
          this.booting.set(false);
          if (!this.goToRecovery(null, error)) this.errorKey.set(GENERIC_ERROR);
        },
      });
  }

  private completeLogin(tokens: SessionTokens | null): void {
    if (!tokens) {
      this.errorKey.set(GENERIC_ERROR);
      return;
    }

    if (this.mcpTransaction){
      return this.completeMcpLogin(tokens, this.mcpTransaction);
    } 

    this.loading.set(true);
    this.postLogin
      .run(tokens, { returnUrl: this.returnUrl, queryParams: this.queryParams, customerParam: this.customerParam })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.booting.set(false),
        error: () => {
          this.loading.set(false);
          this.booting.set(false);
          this.utilService.ga('login', 'click', 'error_profile');
          this.errorKey.set(GENERIC_ERROR);
        },
      });
  }

  private goToRecovery(recovery: any, raw: any): boolean {
    const descriptor = recovery ?? { recoveryToken: raw?.recoveryToken ?? raw?.recovery_token, recoveryType: null };
    if (!descriptor.recoveryToken) return false;

    void this.router.navigate([isPasswordExpired(descriptor, raw) ? '/password-expired' : '/recovery'], {
      queryParams: { token: descriptor.recoveryToken, language: this.utilService.getCurrentLanguage() },
    });
    this.loading.set(false);
    return true;
  }

  private handleCredentialsError(error: any): void {
    this.loading.set(false);
    if (this.goToRecovery(null, error)) return;

    this.utilService.ga('login', 'click', 'error_credentials');

    if (error?.details?.includes(String(ErrorCodes.INVALID_CREDENTIALS)) || this.isMissingAccount(error)) {
      const match = /(\d+) attempts left/.exec(error.message ?? '');
      if (match) this.remainingAttempts = parseInt(match[1], 10);

      if (this.remainingAttempts === 0) return this.errorKey.set('errors.login.blocked');
      if (this.remainingAttempts === null) return this.errorKey.set('auth_login.errors.invalid_credentials');

      this.errorParams.set({ attempts: this.remainingAttempts });
      this.errorKey.set('errors.login.invalid_value');
      return;
    }
    if (error?.details?.includes(String(ErrorCodes.BLOCKED_USER))) return this.errorKey.set('errors.login.blocked');

    this.errorKey.set(GENERIC_ERROR);
  }

  private isMcpLogin(params: Record<string, string>): boolean {
    if (this.validMcpTransaction(params['mcp_transaction'])) return true;
    // Al volver del SSO la transacción no viene en la URL: quedó en el contexto guardado antes de salir al proveedor.
    return !!(params['code'] && params['state'] && this.validMcpTransaction(this.ssoContext.pendingMcpTransaction(params['state'])));
  }

  private validMcpTransaction(raw: unknown): string | null {
    return typeof raw === 'string' && /^[A-Za-z0-9_-]{16,128}$/.test(raw) ? raw : null;
  }

  private completeMcpLogin(tokens: SessionTokens, mcpTransaction: string): void {
    this.loading.set(true);
    this.authFlow
      .createMcpHandoff(mcpTransaction, tokens.token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ handoff_code }) => {
          const callback = new URL(environment.mcpCallbackUrl);
          callback.searchParams.set('mcp_transaction', mcpTransaction);
          callback.searchParams.set('handoff_code', handoff_code);
          window.location.href = callback.toString();
        },
        error: () => {
          this.loading.set(false);
          this.booting.set(false);
          this.errorKey.set('auth_login.mcp.failed');
        },
      });
  }
}
